import { useMemo, useState } from "react";
import toast from "react-hot-toast";
import {
  ArrowRightLeft,
  Bot,
  ExternalLink,
  MessageSquare,
  RefreshCw,
  Search,
  UserCheck,
  UserX,
  Workflow,
} from "lucide-react";
import { useActiveService } from "../hooks/useActiveService";
import { useUsers } from "../hooks/useUsers";
import { useConversationsApi } from "../api/conversationsApi";
import { contactLabel, formatPhone } from "../components/analysis/analysisLabels";
import { formatDuration, getStatusColor } from "../utils/helpers";

const PERIODS = [
  { value: 4, label: "4 h" },
  { value: 24, label: "24 h" },
  { value: 72, label: "3 dias" },
  // Sem recorte de tempo: é o modo para redistribuir carteira, quando conversa
  // aberta e parada há semanas também precisa entrar na lista.
  { value: 0, label: "Todas em aberto" },
];

const HANDLERS = {
  todos: { label: "Todas", match: () => true },
  robo: { label: "Com o robô", match: (item) => item.handler === "robo" },
  atendente: { label: "Com atendente", match: (item) => item.handler === "atendente" },
  ninguem: { label: "Sem ninguém", match: (item) => item.handler === "ninguem" },
  aguardando: { label: "Aguardando resposta", match: (item) => item.awaitingUs },
};

const HANDLER_BADGE = {
  robo: {
    label: "Robô",
    icon: Bot,
    className: "bg-blue-100 text-blue-800 dark:bg-blue-900/30 dark:text-blue-300",
  },
  atendente: {
    label: "Atendente",
    icon: UserCheck,
    className: "bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-300",
  },
  ninguem: {
    label: "Sem responsável",
    icon: UserX,
    className: "bg-amber-100 text-amber-800 dark:bg-amber-900/30 dark:text-amber-300",
  },
};

const StatCard = ({ icon: Icon, tone, value, label, hint, active, alert, onClick }) => (
  <button
    onClick={onClick}
    className={`rounded-lg border bg-white p-4 text-left shadow-sm transition-colors hover:border-gray-400 dark:bg-gray-900 dark:hover:border-gray-500 ${
      active
        ? "border-green-500 ring-2 ring-green-500/40"
        : alert
          ? "border-amber-300 dark:border-amber-800"
          : "border-gray-200 dark:border-gray-700"
    }`}
  >
    <div className="mb-1 flex items-center justify-between">
      <Icon className={`h-5 w-5 ${tone}`} />
      <span className="text-2xl font-bold text-gray-900 dark:text-gray-100">{value}</span>
    </div>
    <p className="text-sm font-medium text-gray-700 dark:text-gray-300">{label}</p>
    {hint && <p className="mt-0.5 text-xs text-gray-500 dark:text-gray-400">{hint}</p>}
  </button>
);

const ServiceRow = ({ item, selected, onToggleSelect, onOpen }) => {
  const name = contactLabel(item.contact);
  const phone = formatPhone(item.contact?.phoneNumber);
  const badge = HANDLER_BADGE[item.handler];
  const HandlerIcon = badge.icon;

  return (
    <div
      className={`rounded-lg border bg-white p-4 shadow-sm dark:bg-gray-900 ${
        item.awaitingUs
          ? "border-l-4 border-l-amber-400 border-gray-200 dark:border-gray-700"
          : "border-gray-200 dark:border-gray-700"
      }`}
    >
      <div className="mb-2 flex flex-wrap items-start justify-between gap-2">
        <div className="flex min-w-0 gap-3">
          <input
            type="checkbox"
            checked={selected}
            onChange={() => onToggleSelect(item.conversationId)}
            className="mt-1 h-4 w-4 shrink-0 cursor-pointer accent-green-500"
            aria-label={`Selecionar ${name}`}
          />

          <div className="min-w-0">
          <div className="flex flex-wrap items-center gap-2">
            <p className="truncate font-semibold text-gray-900 dark:text-gray-100">{name}</p>

            <span className={`flex items-center gap-1 rounded-full px-2.5 py-1 text-xs font-medium ${badge.className}`}>
              <HandlerIcon className="h-3 w-3" />
              {badge.label}
            </span>

            <span className={`rounded-full px-2.5 py-1 text-xs font-medium ${getStatusColor(item.status)}`}>
              {item.status}
            </span>

            {item.awaitingUs && (
              <span className="rounded-full bg-amber-100 px-2.5 py-1 text-xs font-medium text-amber-800 dark:bg-amber-900/30 dark:text-amber-300">
                Aguardando nossa resposta
              </span>
            )}
          </div>

          <p className="mt-0.5 text-xs text-gray-500 dark:text-gray-400">
            {phone && phone !== name && `${phone} · `}
            última mensagem há {formatDuration(item.idleMinutes * 60)} ·{" "}
            {item.messageCount} mensagens
            {item.assignedUsers?.length > 0 &&
              ` · ${item.assignedUsers.map((user) => user.name).join(", ")}`}
          </p>
          </div>
        </div>

        <button
          onClick={() => onOpen?.(item.conversationId)}
          className="flex shrink-0 items-center gap-1 rounded-lg border border-gray-300 px-3 py-1.5 text-sm text-gray-700 transition-colors hover:bg-gray-50 dark:border-gray-600 dark:text-gray-300 dark:hover:bg-gray-800"
        >
          <ExternalLink className="h-4 w-4" />
          Abrir
        </button>
      </div>

      {/* Os dois "fluxos" da conversa: onde ela está no funil e onde está no
          roteiro. São coisas diferentes e o gestor precisa das duas. */}
      <div className="flex flex-wrap items-center gap-2 border-t border-gray-100 pt-2 dark:border-gray-800">
        <span className="flex items-center gap-1 rounded-full bg-purple-100 px-2.5 py-1 text-xs font-medium text-purple-800 dark:bg-purple-900/30 dark:text-purple-300">
          <Workflow className="h-3 w-3" />
          {item.funnel || "Sem etapa no funil"}
        </span>

        {item.script ? (
          <span className="flex items-center gap-1 rounded-full bg-gray-100 px-2.5 py-1 text-xs font-medium text-gray-700 dark:bg-gray-800 dark:text-gray-300">
            <Bot className="h-3 w-3" />
            {item.script.name}: {item.script.stepLabel}
            {item.script.stepIndex && ` (${item.script.stepIndex}/${item.script.totalSteps})`}
            {!item.script.active && " — parado"}
          </span>
        ) : (
          <span className="text-xs text-gray-400">Sem roteiro</span>
        )}

        {item.tags?.map((tag) => (
          <span
            key={tag.id}
            className="rounded-full px-2 py-0.5 text-[11px] font-medium"
            style={{ backgroundColor: tag.backgroundColor, color: tag.textColor }}
          >
            {tag.name}
          </span>
        ))}
      </div>
    </div>
  );
};

const ActiveService = ({ onOpenConversation }) => {
  const [hours, setHours] = useState(24);
  const [view, setView] = useState("todos");
  const [search, setSearch] = useState("");
  const [owner, setOwner] = useState("all");
  const [selected, setSelected] = useState(() => new Set());
  const [target, setTarget] = useState("");
  const [keepCurrent, setKeepCurrent] = useState(false);
  const [transferring, setTransferring] = useState(false);
  const { data, loading, error, refetch } = useActiveService(hours);
  const { users } = useUsers();
  const api = useConversationsApi();

  const items = useMemo(() => {
    if (!data) return [];
    const term = search.trim().toLowerCase();
    const matcher = HANDLERS[view]?.match || HANDLERS.todos.match;

    return data.items.filter((item) => {
      if (!matcher(item)) return false;

      if (owner === "none" && item.assignedUsers?.length) return false;
      if (owner !== "all" && owner !== "none") {
        if (!item.assignedUsers?.some((user) => String(user.id) === owner)) return false;
      }

      if (!term) return true;
      return [item.contact?.name, item.contact?.phoneNumber, item.funnel, item.script?.name]
        .filter(Boolean)
        .some((value) => String(value).toLowerCase().includes(term));
    });
  }, [data, view, owner, search]);

  const toggleSelect = (conversationId) =>
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(conversationId)) next.delete(conversationId);
      else next.add(conversationId);
      return next;
    });

  // Trocar de recorte muda o conjunto visível; manter a seleção antiga faria a
  // transferência atingir conversas que saíram da tela.
  const changeFilter = (setter) => (value) => {
    setter(value);
    setSelected(new Set());
  };

  const handleTransfer = async () => {
    const ids = items
      .map((item) => item.conversationId)
      .filter((id) => selected.has(id));

    if (!ids.length || !target) return;

    const destination = users.find((user) => String(user.id) === target);
    const confirmed = window.confirm(
      `Transferir ${ids.length} conversa(s) para ${destination?.name}?\n\n`
      + (keepCurrent
        ? "Os responsáveis atuais serão mantidos."
        : "Os responsáveis atuais serão substituídos."),
    );

    if (!confirmed) return;

    setTransferring(true);
    try {
      const result = await api.transferBulk(
        ids,
        [Number(target)],
        keepCurrent ? "add" : "replace",
      );

      await refetch();
      setSelected(new Set());
      toast.success(`${result.transferred} conversa(s) transferidas.`);

      if (result.transferred < result.requested) {
        toast(
          `${result.requested - result.transferred} não foram alteradas por falta de acesso.`,
          { icon: "⚠️" },
        );
      }
    } catch (err) {
      toast.error(err.message || "Não foi possível transferir.");
    } finally {
      setTransferring(false);
    }
  };

  if (loading && !data) {
    return (
      <div className="flex flex-1 items-center justify-center bg-gray-50 dark:bg-gray-800">
        <p className="text-gray-600 dark:text-gray-400">Carregando atendimentos...</p>
      </div>
    );
  }

  if (error) {
    return (
      <div className="flex flex-1 items-center justify-center bg-gray-50 dark:bg-gray-800">
        <p className="text-red-500">Erro ao carregar: {error}</p>
      </div>
    );
  }

  if (!data) return null;

  const { summary, byStage } = data;
  const stages = Object.entries(byStage).sort((a, b) => b[1] - a[1]);
  const visibleSelected = items.filter((item) => selected.has(item.conversationId)).length;
  const allVisibleSelected = items.length > 0 && visibleSelected === items.length;
  const someVisibleSelected = visibleSelected > 0;

  return (
    <div className="flex-1 overflow-y-auto bg-gray-50 p-6 transition-colors duration-300 dark:bg-gray-800">
      <div className="mx-auto max-w-5xl">
        <div className="mb-6 flex flex-wrap items-center justify-between gap-3">
          <div>
            <h1 className="text-2xl font-bold text-gray-900 dark:text-gray-100">
              Em Atendimento
            </h1>
            <p className="text-sm text-gray-600 dark:text-gray-400">
              {data.window.unlimited
                ? "Todas as conversas em aberto, sem recorte de tempo"
                : `Conversas com atividade nas últimas ${data.window.hours} horas`}
              , quem está conduzindo e em que ponto elas estão · atualiza a cada
              minuto
            </p>
          </div>

          <div className="flex items-center gap-2">
            <div className="flex rounded-lg border border-gray-300 p-0.5 dark:border-gray-600">
              {PERIODS.map((period) => (
                <button
                  key={period.value}
                  onClick={() => setHours(period.value)}
                  className={`rounded-md px-3 py-1.5 text-sm transition-colors ${
                    hours === period.value
                      ? "bg-green-500 text-white"
                      : "text-gray-600 hover:bg-gray-100 dark:text-gray-400 dark:hover:bg-gray-700"
                  }`}
                >
                  {period.label}
                </button>
              ))}
            </div>

            <button
              onClick={refetch}
              className="flex items-center gap-1.5 rounded-lg bg-green-500 px-4 py-2 text-sm text-white transition-colors hover:bg-green-600"
            >
              <RefreshCw className={`h-4 w-4 ${loading ? "animate-spin" : ""}`} />
              Atualizar
            </button>
          </div>
        </div>

        <div className="mb-4 grid grid-cols-2 gap-4 lg:grid-cols-4">
          <StatCard
            icon={MessageSquare}
            tone="text-gray-500"
            value={summary.total}
            label="Em atendimento"
            hint="Todas as ativas"
            active={view === "todos"}
            onClick={() => changeFilter(setView)("todos")}
          />
          <StatCard
            icon={Bot}
            tone="text-blue-500"
            value={summary.robo}
            label="Com o robô"
            hint="Roteiro conduzindo"
            active={view === "robo"}
            onClick={() => changeFilter(setView)("robo")}
          />
          <StatCard
            icon={UserCheck}
            tone="text-green-500"
            value={summary.atendente}
            label="Com atendente"
            hint="Alguém responsável"
            active={view === "atendente"}
            onClick={() => changeFilter(setView)("atendente")}
          />
          <StatCard
            icon={UserX}
            tone="text-amber-500"
            value={summary.ninguem}
            label="Sem ninguém"
            hint="Sem robô e sem responsável"
            alert={summary.ninguem > 0}
            active={view === "ninguem"}
            onClick={() => changeFilter(setView)("ninguem")}
          />
        </div>

        {stages.length > 0 && (
          <div className="mb-4 flex flex-wrap items-center gap-2 rounded-lg border border-gray-200 bg-white p-3 dark:border-gray-700 dark:bg-gray-900">
            <span className="text-xs font-semibold uppercase text-gray-500 dark:text-gray-400">
              Por etapa do funil
            </span>
            {stages.map(([stage, count]) => (
              <span
                key={stage}
                className="rounded-full bg-purple-100 px-2.5 py-1 text-xs font-medium text-purple-800 dark:bg-purple-900/30 dark:text-purple-300"
              >
                {stage}: {count}
              </span>
            ))}
          </div>
        )}

        <div className="mb-4 flex flex-wrap items-center gap-2">
          <select
            value={owner}
            onChange={(event) => changeFilter(setOwner)(event.target.value)}
            className="rounded-lg border border-gray-300 bg-white py-2 pl-3 pr-8 text-sm text-gray-900 outline-none focus:border-green-500 focus:ring-2 focus:ring-green-500 dark:border-gray-600 dark:bg-gray-800 dark:text-gray-100"
            aria-label="Filtrar por responsável"
          >
            <option value="all">Todos os responsáveis</option>
            <option value="none">Sem responsável</option>
            {users.map((user) => (
              <option key={user.id} value={String(user.id)}>{user.name}</option>
            ))}
          </select>

          <label className="flex cursor-pointer items-center gap-2 text-sm text-gray-600 dark:text-gray-400">
            <input
              type="checkbox"
              className="h-4 w-4 cursor-pointer accent-green-500"
              checked={allVisibleSelected}
              ref={(node) => {
                if (node) node.indeterminate = someVisibleSelected && !allVisibleSelected;
              }}
              onChange={() =>
                setSelected(
                  allVisibleSelected
                    ? new Set()
                    : new Set(items.map((item) => item.conversationId)),
                )
              }
              disabled={items.length === 0}
            />
            Selecionar todos
          </label>

          <button
            onClick={() => changeFilter(setView)("aguardando")}
            className={`rounded-full px-3 py-1 text-xs font-medium transition-colors ${
              view === "aguardando"
                ? "bg-gray-900 text-white dark:bg-gray-100 dark:text-gray-900"
                : "bg-amber-100 text-amber-800 hover:bg-amber-200 dark:bg-amber-900/30 dark:text-amber-300"
            }`}
          >
            Aguardando nossa resposta ({summary.awaitingUs})
          </button>

          <div className="relative ml-auto w-56">
            <Search className="absolute left-3 top-2.5 h-4 w-4 text-gray-400" />
            <input
              type="text"
              placeholder="Cliente, etapa, roteiro..."
              value={search}
              onChange={(event) => setSearch(event.target.value)}
              className="w-full rounded-lg border border-gray-300 bg-white py-2 pl-9 pr-3 text-sm text-gray-900 placeholder-gray-400 outline-none focus:border-green-500 focus:ring-2 focus:ring-green-500 dark:border-gray-600 dark:bg-gray-800 dark:text-gray-100"
            />
          </div>

          <p className="w-full text-sm text-gray-600 dark:text-gray-400">
            {items.length} conversa{items.length === 1 ? "" : "s"} ·{" "}
            {HANDLERS[view]?.label}
          </p>
        </div>

        {selected.size > 0 && (
          <div className="mb-4 flex flex-wrap items-center gap-2 rounded-lg border border-green-300 bg-green-50 p-3 dark:border-green-800 dark:bg-green-900/20">
            <span className="flex items-center gap-1.5 text-sm font-medium text-green-900 dark:text-green-200">
              <ArrowRightLeft className="h-4 w-4" />
              {selected.size} selecionada{selected.size === 1 ? "" : "s"}
            </span>

            <select
              value={target}
              onChange={(event) => setTarget(event.target.value)}
              className="rounded-lg border border-gray-300 bg-white py-1.5 pl-3 pr-8 text-sm text-gray-900 outline-none focus:border-green-500 dark:border-gray-600 dark:bg-gray-800 dark:text-gray-100"
            >
              <option value="">Transferir para...</option>
              {users.map((user) => (
                <option key={user.id} value={String(user.id)}>{user.name}</option>
              ))}
            </select>

            <label className="flex items-center gap-1.5 text-xs text-green-900 dark:text-green-200">
              <input
                type="checkbox"
                className="h-4 w-4 accent-green-600"
                checked={keepCurrent}
                onChange={(event) => setKeepCurrent(event.target.checked)}
              />
              Manter os responsáveis atuais
            </label>

            <button
              onClick={handleTransfer}
              disabled={transferring || !target}
              className="rounded-lg bg-green-600 px-3 py-1.5 text-sm text-white transition-colors hover:bg-green-700 disabled:opacity-60"
            >
              {transferring ? "Transferindo..." : "Transferir"}
            </button>

            <button
              onClick={() => setSelected(new Set())}
              disabled={transferring}
              className="ml-auto text-sm text-green-900 underline transition-colors hover:text-green-700 disabled:opacity-60 dark:text-green-200"
            >
              Limpar seleção
            </button>
          </div>
        )}

        <div className="space-y-3">
          {items.length === 0 ? (
            <div className="rounded-lg border border-dashed border-gray-300 p-10 text-center dark:border-gray-600">
              <p className="text-gray-600 dark:text-gray-400">
                Nenhuma conversa nesta seleção.
              </p>
            </div>
          ) : (
            items.map((item) => (
              <ServiceRow
                key={item.conversationId}
                item={item}
                selected={selected.has(item.conversationId)}
                onToggleSelect={toggleSelect}
                onOpen={onOpenConversation}
              />
            ))
          )}
        </div>

        {summary.truncated && (
          <p className="mt-4 text-xs text-amber-600 dark:text-amber-400">
            A lista atingiu o limite de exibição. Filtre por responsável ou
            reduza a janela de tempo para trabalhar em partes — a transferência
            só alcança o que está na tela.
          </p>
        )}
      </div>
    </div>
  );
};

export default ActiveService;
